<template>
  <div class="my-profile-sections">
    <div v-for="(section, index) in getProfileSections" :key="`sec-${index}`" class="profile-section">
      <GamNavItemLink
        v-if="isMobile && false"
        name="feed"
        :display-name="'News'"
        :variant="GamNavVariant.SECONDARY"
        @click="() => router.push({ name: RouterNameType.NEWS })"
        :messages="{ count: unreadCount }"
        show-arrow
        is-news
      />

      <gam-nav-item-link v-for="sectionItem in section" :key="`sec-i-${sectionItem.position}`" v-bind="sectionItem" />
    </div>
  </div>

  <GeneralSection :is-open="showGeneral" :on-update="updateUserInfo" @close="closeGeneral" @update="updateUserInfo" />

  <RatingsSection :is-open="showRatings" :on-update="updateUserInfo" @close="closeRatings" @update="updateUserInfo" />

  <NotificationPreferences :is-open="showNotifications" @close="closeNotifications" />

  <EmailPreferences :is-open="showEmailPrefs" @close="closeEmailPrefs" />

  <BlockedUsersSection :is-open="showBlocked" @close="closeBlocked" />
</template>

<script setup lang="ts">
import { RouterNameType, type UIMyProfileSectionItem } from '@/core/data/config/uiConfig.interface';
import { RatingType } from '@/core/data/rating/rating.type';
import { gambits } from '@/core/gambits';
import ProfileHelper from '@/core/helpers/profile.helper';
import { areNotificationsSupported, isMobile } from '@/core/helpers/ui.helper';
import { useAuthStore } from '@/stores/auth.store';
import { useNotificationStore } from '@/stores/notification.store';
import GamNavItemLink from '@/views/components/gam-nav/GamNavItemLink.vue';
import { GamNavMyProfileSection, GamNavVariant } from '@/views/composables/constants/components/gamNav.constants';
import { GamRatingPlatform } from '@/views/composables/constants/main/ratings.constants';
import type { GamChipType } from '@/views/composables/models/components/GamChip';
import type { GamNavItemLinkType } from '@/views/composables/models/components/GamNav';
import { storeToRefs } from 'pinia';
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import BlockedUsersSection from './sections/BlockedUsersSection.vue';
import EmailPreferences from './sections/EmailPreferences.vue';
import GeneralSection from './sections/GeneralSection.vue';
import NotificationPreferences from './sections/NotificationPreferences.vue';
import RatingsSection from './sections/RatingsSection.vue';

const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);
const profileSections = gambits.configService.getMyProfileSections();
const notificationStore = useNotificationStore();
const { unreadCount } = storeToRefs(notificationStore);

const router = useRouter();
const route = useRoute();

const showGeneral = ref(false);
const showRatings = ref(false);
const showNotifications = ref(false);
const showEmailPrefs = ref(false);
const showBlocked = ref(false);

const updateUserInfo = async () => {
  await authStore.fetchUserInfo();
};

const setSidePanel = (value: string) => {
  router.replace({ path: route.path, query: { ...route.query, side_panel: value } });
};

const clearSidePanel = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { side_panel, ...query } = route.query;
  router.replace({ path: route.path, query });
};

const openGeneral = () => {
  setSidePanel('general');
  showGeneral.value = true;
};

const closeGeneral = () => {
  clearSidePanel();
  showGeneral.value = false;
};

const openRatings = () => {
  setSidePanel('ratings');
  showRatings.value = true;
};

const closeRatings = () => {
  clearSidePanel();
  showRatings.value = false;
};

const openNotificationPreferences = () => {
  setSidePanel('notification_settings');
  showNotifications.value = true;
};

const closeNotifications = () => {
  clearSidePanel();
  showNotifications.value = false;
};

const openEmailPreferences = () => {
  setSidePanel('email_preferences');
  showEmailPrefs.value = true;
};

const closeEmailPrefs = () => {
  clearSidePanel();
  showEmailPrefs.value = false;
};

const openBlocked = () => {
  setSidePanel('blocked');
  showBlocked.value = true;
};

const closeBlocked = () => {
  clearSidePanel();
  showBlocked.value = false;
};

const checkOpenSection = () => {
  if (gambits.ratingService.getConnect() === GamRatingPlatform.LICHESS) {
    openRatings();
  }
};

const openSettings = (section: GamNavMyProfileSection) => {
  switch (section) {
    case GamNavMyProfileSection.RATINGS:
      openRatings();
      break;
    case GamNavMyProfileSection.GENERAL:
      openGeneral();
      break;
    case GamNavMyProfileSection.BLOCKED:
      openBlocked();
      break;
    case GamNavMyProfileSection.NOTIFICATION:
      openNotificationPreferences();
      break;
    case GamNavMyProfileSection.EMAIL_PREFERENCES:
      openEmailPreferences();
      break;
    default:
      break;
  }
};

const getProfileItem = (section: UIMyProfileSectionItem): GamNavItemLinkType => {
  return {
    name: section.name,
    displayName: `profile.my.${section.name}.title`,
    variant: GamNavVariant.SECONDARY,
    showArrow: true,
    statusChips: getSectionItemStatus(section.name),
    emoji: section.name === GamNavMyProfileSection.GENERAL ? userDetails.value?.emoji : undefined,
    onClick: () => {
      openSettings(section.name);
    },
  };
};

const getSectionItemStatus = (section: GamNavMyProfileSection): GamChipType[] | undefined => {
  switch (section) {
    case GamNavMyProfileSection.RATINGS: {
      const previewRatings = userDetails.value?.ratings?.filter((r) => r.type === RatingType.GAMBIT);
      return ProfileHelper.getProfileRatings(previewRatings, userDetails.value?.ratingDisplayMode);
    }
    case GamNavMyProfileSection.AVAILABILITY: {
      return ProfileHelper.getProfileAvailability(userDetails.value?.preferredPlayingTime);
    }
    default: {
      return;
    }
  }
};

const shouldDisplaySectionItem = (section: UIMyProfileSectionItem): boolean => {
  if (section.name === 'blocked') {
    return !!userDetails.value?.blocked?.length;
  }

  if (section.name === GamNavMyProfileSection.NOTIFICATION) {
    return areNotificationsSupported.value;
  }

  return true;
};

const getProfileSections = computed((): GamNavItemLinkType[][] => {
  if (!profileSections) return [];
  return profileSections.map((section) => {
    return section.filter(shouldDisplaySectionItem).map((sectionItem) => {
      return getProfileItem(sectionItem);
    });
  });
});

watch(
  () => route.query.side_panel,
  (value) => {
    switch (value) {
      case 'general':
        showGeneral.value = true;
        break;
      case 'ratings':
        showRatings.value = true;
        break;
      case 'notification_settings':
        if (areNotificationsSupported.value) showNotifications.value = true;
        break;
      case 'email_preferences':
        showEmailPrefs.value = true;
        break;
      case 'blocked':
        showBlocked.value = true;
        break;
    }
  },
  { immediate: true },
);

watch(userDetails, () => {
  nextTick(() => {
    checkOpenSection();
  });
});
</script>

<style scoped lang="scss">
@use '@/ui/css/partial';

.my-profile-sections {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-md);
  align-self: stretch;

  .profile-section {
    display: flex;
    padding: 0 var(--spacing-l);
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    border-radius: var(--radius-small);
    border: 1px solid var(--color-pink);

    &:before {
      background: var(--color-linear-gradient);
    }
  }
}
</style>
